/**
 * 描画負荷の品質プリセット。
 * ピクセル比・太陽光 CSM・ReSTIR リザーバ・点光源デノイズ・解析 SDF・被写界深度・bloom をまとめて切り替える。
 */

/** 地面が見える距離（フォグで完全に隠れる手前）。CSM 影と点光源の到達もこれに揃える。 */
export const GROUND_DRAW_DISTANCE = 420;

export type GraphicsQualityId = 'low' | 'medium' | 'high';

export interface GraphicsQuality {
  id: GraphicsQualityId;
  label: string;
  /** デバイスピクセル比の上限 */
  pixelRatio: number;
  /** 太陽光の CSM 影マップを焼く。 */
  shadows: boolean;
  softShadows: boolean;
  shadowMapSize: number;
  /** 有効にする CSM カスケード数（作成済みライトのうち先頭から） */
  shadowCascadesActive: number;
  /** ReSTIR の光源テクスチャに載せる上限。 */
  maxPointLights: number;
  /** リザーバパスの解像度倍率（1 がフル解像度）。 */
  restirScale: number;
  /** 点光源デノイザの A-trous 反復。奇数。 */
  denoiserAtrousIterations: number;
  /** カメラ周辺 SDF 立方体の一辺のボクセル数。 */
  voxelResolution: number;
  /** カメラ周辺 SDF 立方体の一辺（メートル）。 */
  voxelExtent: number;
  /** SDF 可視レイの最大ステップ。 */
  sdfMaxSteps: number;
  /** 被写界深度の Vogel サンプル数 */
  depthOfFieldSamples: number;
  /** 画面高さに対する最大ボケ半径 */
  depthOfFieldMaxBlur: number;
  /** HDR bloom の加算強度 */
  bloomStrength: number;
  /** bloom の mip 段数（多いほど広くにじむ） */
  bloomMips: number;
}

export const GRAPHICS_PRESETS: Record<GraphicsQualityId, GraphicsQuality> = {
  low: {
    id: 'low',
    label: '低',
    pixelRatio: 1,
    shadows: false,
    softShadows: false,
    shadowMapSize: 512,
    shadowCascadesActive: 1,
    maxPointLights: 64,
    restirScale: 0.4,
    denoiserAtrousIterations: 1,
    voxelResolution: 48,
    voxelExtent: 16,
    sdfMaxSteps: 32,
    depthOfFieldSamples: 12,
    depthOfFieldMaxBlur: 0.004,
    bloomStrength: 0.45,
    bloomMips: 3,
  },
  medium: {
    id: 'medium',
    label: '中',
    pixelRatio: 1.5,
    shadows: true,
    softShadows: true,
    shadowMapSize: 1024,
    shadowCascadesActive: 2,
    maxPointLights: 128,
    restirScale: 0.5,
    denoiserAtrousIterations: 3,
    voxelResolution: 64,
    voxelExtent: 24,
    sdfMaxSteps: 48,
    depthOfFieldSamples: 16,
    depthOfFieldMaxBlur: 0.006,
    bloomStrength: 0.7,
    bloomMips: 4,
  },
  high: {
    id: 'high',
    label: '高',
    pixelRatio: 2,
    shadows: true,
    softShadows: true,
    shadowMapSize: 2048,
    shadowCascadesActive: 3,
    maxPointLights: 256,
    restirScale: 0.5,
    denoiserAtrousIterations: 3,
    voxelResolution: 96,
    voxelExtent: 32,
    sdfMaxSteps: 64,
    depthOfFieldSamples: 22,
    depthOfFieldMaxBlur: 0.008,
    bloomStrength: 0.85,
    bloomMips: 5,
  },
};

/** 既定は「中」。 */
export const DEFAULT_GRAPHICS_QUALITY: GraphicsQualityId = 'medium';

export const GRAPHICS_QUALITY_IDS: GraphicsQualityId[] = ['low', 'medium', 'high'];

export function resolveGraphicsQuality(id: string | null | undefined): GraphicsQuality {
  if (id && id in GRAPHICS_PRESETS) {
    return GRAPHICS_PRESETS[id as GraphicsQualityId];
  }
  return GRAPHICS_PRESETS[DEFAULT_GRAPHICS_QUALITY];
}
